import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Sparkles, Crown, Star, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TIER_VISUALS = {
  basic: { icon: Star, color: "from-amber-400 to-amber-600" },
  hero: { icon: Sparkles, color: "from-primary to-orange-600" },
  premium: { icon: Crown, color: "from-amber-500 via-primary to-rose-600" },
};

const FeaturedSpots = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [myRests, setMyRests] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [openPkg, setOpenPkg] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadAll = async () => {
    try {
      const [p, m, t] = await Promise.all([
        api.get("/featured/packages"),
        api.get("/restaurants/owner/me"),
        api.get("/featured/my-transactions"),
      ]);
      setPackages(p.data);
      setMyRests(m.data);
      setTransactions(t.data);
      if (m.data[0] && !selectedRestaurant) setSelectedRestaurant(m.data[0].id);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!user) navigate("/");
    else loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const purchase = async (pkg) => {
    if (!selectedRestaurant) {
      toast.error("Pilih restoran dulu");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/featured/checkout", {
        package_id: pkg.id,
        restaurant_id: selectedRestaurant,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat checkout");
      setSubmitting(false);
    }
  };

  const formatIDR = (n) => `Rp ${n.toLocaleString("id-ID")}`;
  const formatDateID = (iso) => new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
  const isActive = (until) => until && new Date(until) > new Date();

  return (
    <div data-testid="featured-spots-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Boost Visibility</p>
        <h1 className="font-heading text-3xl font-black mt-2">Featured Spots</h1>
        <p className="text-muted-foreground mt-2">Tingkatkan visibilitas restoranmu di Gading Foodie</p>
      </div>

      {myRests.length === 0 ? (
        <div className="warm-card p-12 text-center">
          <p className="text-muted-foreground mb-4">Daftarkan restoranmu dulu sebelum membeli featured spot.</p>
          <Link to="/dashboard/restaurants/new">
            <Button className="rounded-full">Tambah Restoran</Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="warm-card p-5 mb-6">
            <label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Pilih Restoran</label>
            <Select value={selectedRestaurant} onValueChange={setSelectedRestaurant}>
              <SelectTrigger data-testid="featured-restaurant-select" className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                {myRests.map(r => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name} {isActive(r.featured_until) && <span className="ml-2 text-primary">• {r.featured_tier} aktif</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {packages.map(pkg => {
              const Vis = TIER_VISUALS[pkg.tier] || TIER_VISUALS.basic;
              return (
                <div key={pkg.id} className={`warm-card overflow-hidden flex flex-col ${pkg.tier === "premium" ? "ring-2 ring-primary" : ""}`} data-testid={`pkg-${pkg.id}`}>
                  <div className={`bg-gradient-to-br ${Vis.color} p-6 text-white`}>
                    <div className="flex items-center justify-between mb-3">
                      <Vis.icon className="w-8 h-8" />
                      {pkg.tier === "premium" && <span className="text-[10px] tracking-[0.2em] uppercase font-bold bg-white/20 px-2 py-1 rounded-full">Best Value</span>}
                    </div>
                    <h3 className="font-heading text-2xl font-black">{pkg.name}</h3>
                    <p className="text-sm text-white/90 mt-1">{pkg.tagline}</p>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="font-heading text-4xl font-black text-foreground">{formatIDR(pkg.amount)}</div>
                    <div className="text-sm text-muted-foreground mt-1">{pkg.duration_days} hari</div>
                    <ul className="space-y-2 mt-5 flex-1">
                      {pkg.tier === "basic" && (
                        <>
                          <li className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />Badge "Featured" di hasil search</li>
                          <li className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />Listing diatas hasil pencarian</li>
                        </>
                      )}
                      {pkg.tier === "hero" && (
                        <>
                          <li className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />Tampil di carousel homepage hero</li>
                          <li className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />Akses 5,000+ pengunjung/minggu</li>
                          <li className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />Priority placement</li>
                        </>
                      )}
                      {pkg.tier === "premium" && (
                        <>
                          <li className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />Hero Spot + Basic Featured</li>
                          <li className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />Durasi 30 hari penuh</li>
                          <li className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />Hemat 33% dibanding paket mingguan</li>
                          <li className="flex gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />Priority customer support</li>
                        </>
                      )}
                    </ul>
                    <Button
                      onClick={() => purchase(pkg)}
                      disabled={submitting || !selectedRestaurant}
                      className="rounded-full mt-6 bg-primary hover:bg-primary/90"
                      data-testid={`buy-${pkg.id}`}
                    >
                      Beli Sekarang <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="warm-card p-6 mt-8">
            <h2 className="font-heading text-xl font-black mb-4">Riwayat Transaksi</h2>
            {transactions.length === 0 ? (
              <p className="text-sm text-muted-foreground">Belum ada transaksi.</p>
            ) : (
              <div className="divide-y divide-border" data-testid="transactions-list">
                {transactions.map(t => (
                  <div key={t.session_id} className="py-3 flex items-center justify-between text-sm" data-testid={`tx-${t.session_id}`}>
                    <div>
                      <div className="font-bold">{t.restaurant?.name} • {t.tier?.toUpperCase()}</div>
                      <div className="text-xs text-muted-foreground">{formatDateID(t.created_at)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading font-black">{formatIDR(t.amount)}</div>
                      <div className={`text-xs ${t.payment_status === "paid" ? "text-green-600" : t.payment_status === "pending" ? "text-amber-600" : "text-muted-foreground"}`}>
                        {t.payment_status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FeaturedSpots;
